import { Query, ZenqlQuery } from 'contensis-management-api';
import { Entry } from 'contensis-management-api';
import pl from 'pluralize';

import { MigrateStatus } from '~/models/Migration';
import { searchQuery } from '~/providers/ContensisManagementApi';
import ContensisRepository from '~/repositories/ContensisRepository';
import { AssetEntryBuilder } from '~/transformations/AssetEntryBuilder';
import { getCmsFilePath } from '~/util/EntryHelper';
import { dedupeInner } from '~/util/array';
import { Logger, completeProgressBar, newProgressBar } from '~/util/logger';
import { merge } from '~/util/merge';

export interface IAssetEntry {
  contentTypeId?: string;
  diff?: string;
  entryTitle: string;
  existing: Entry | undefined;
  filePath: string | undefined;
  finalEntry: Entry;
  id: string | undefined;
  originalId?: string;
  shouldPublish?: boolean;
  status: MigrateStatus;
  error?: MappedError;
}

class AssetsService {
  contensis: ContensisRepository;
  source: ContensisRepository;

  constructor(contensis: ContensisRepository, source: ContensisRepository) {
    this.contensis = contensis;
    this.source = source;
  }

  GetAssetEntries = async (entryIds: string[] = []) => {
    Logger.info(
      `Fetching asset entries from project '${this.contensis.projectId}'`
    );
    const loadedEntryIds = [] as [string, string, string, string, string][];
    const batchSize = 100;

    const entries = await this.GetAssetEntriesAllPages(batchSize, entryIds);
    entries.map((e) =>
      loadedEntryIds.push([
        e.sys.contentTypeId || '',
        e.sys.id || '',
        e.entryTitle,
        e.sys.uri || '',
        getCmsFilePath(e) || '',
      ])
    );

    // TODO: performance: this looks like it could be slow when working with large datasets
    this.contensis.entryIds = dedupeInner(
      merge(this.contensis.entryIds || [], loadedEntryIds)
    );
    this.contensis.entries.raw = dedupeInner(
      merge(this.contensis.entries.raw, entries)
    );
    return this.contensis.entries.raw;
  };

  GetAssetEntriesAllPages = async (pageSize = 100, entryIds: string[] = []) => {
    let pageIndex = 0;
    let fetchMore = true;
    const items: Entry[] = [];

    // The while loop is supposed to fetch all entries until
    // we have retrieved the totalCount
    while (fetchMore) {
      // These indices allow us to slice a supplied list of ids
      // and page through the id list to give us the found results
      const startIndex = pageIndex * pageSize;
      const endIndex = Math.min(entryIds.length, (pageIndex + 1) * pageSize);

      const { items: entries, totalCount } = await this.GetAssetEntriesPage(
        pageSize,
        entryIds.length > 0 ? 0 : pageIndex,
        entryIds?.slice(startIndex, endIndex)
      );

      items.push(...entries);

      if (
        (endIndex && endIndex >= entryIds?.length) ||
        (!endIndex && items.length >= totalCount)
      )
        fetchMore = false;
      else pageIndex++;
    }

    return items;
  };

  GetAssetEntriesPage = async (
    pageSize = 200,
    pageIndex = 0,
    entryIds: string[] = []
  ) => {
    let query: Query | ZenqlQuery;
    if (this.contensis.zenQL) {
      query = new ZenqlQuery(this.contensis.zenQL);
      query.pageIndex = pageIndex;
      query.pageSize = pageSize;
    } else if (entryIds.length) {
      query = searchQuery({
        includeIds: entryIds,
        pageIndex,
        pageSize,
      });
    } else {
      query = searchQuery({
        ...this.contensis.query,
        assetTypes: this.source.query?.assetTypes?.length
          ? this.source.query.assetTypes
          : ContensisRepository.assetTypes,
        pageIndex,
        pageSize,
      });
    }
    const pagedResult = await this.contensis.repo.SearchEntries(query);
    return pagedResult;
  };

  BuildAssetEntries = async (outputProgress = false) => {
    const { contensis, source } = this;
    const assetEntries = source.entries.raw.filter(
      (e) => e.sys.dataFormat === 'asset'
    );
    if (assetEntries.length)
      Logger.info(
        `Building ${pl('asset entry', assetEntries.length, true)}`,
        true
      );

    const pr = newProgressBar(
      ` Building assets [${
        this.contensis.project.id
      }] :bar :percent ${Logger.infoText(':total :elapseds/:etas :ratep/s')}`,
      {
        output: outputProgress && !!(assetEntries.length >= 500),
        total: assetEntries.length || 0,
      }
    );

    for (const entry of assetEntries) {
      // Build asset entry
      const entryBuilder = new AssetEntryBuilder(contensis, source, entry);
      entryBuilder.build();
      contensis.entries.migrate.push(entryBuilder);

      pr?.tick();
    }

    completeProgressBar();
  };
}

export default AssetsService;
